import type { DashboardModuleResponsiveDTO } from "src/shared/models/module-info.model";
import type { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import type SlideoutSettingsManager from "src/shared/models/slideout-settings-manager";
import type { BaseGroupType } from "src/AngularJS/Models/group-type.model";
import type { AttributeSelectorModel } from "src/shared/models/attribute-selector.models";
import type { GroupSelectorDTO } from "src/AngularJS/Reusables/group-selector.models";
import type { BareMetricsCalendarPreset } from "src/app/reusables/baremetrics-calendar/baremetrics-calendar.component";
import { Dashboard } from "../dashboard.model";
import { MetricType } from "../../../app/reusables/metric-selector/metric-selector.model";
import { FilterableField } from "../pipeline-config/reporting-sets.models";
import { FilterableGroupingDTO } from "src/app/reusables/group-view-by-selector/group-view-by-selector.component";
import { TimeType } from "../metric";

export class SlideoutSettingsOptions {
    includeDateSelector = false;
    includeDaysOfWeek = false;
    includeTimeSelector = false;
    includeIntervalSelector = false;
    includeEmpGroupSelector = false;
    includeRecGroupSelector = false;
    includeViewBy = false;
    enableMultiGroupSelect = false;
    includeMetricSelector = false;
    includeAttributesSelector = false;
    includeSoloUserAttributesSelector = false;
    includeUserAttMetrics = false;

    public static fromModuleDto(dto: DashboardModuleResponsiveDTO) {
        let options = new SlideoutSettingsOptions();
        options.includeDateSelector = dto.SettingDateRangeSelector;
        options.includeDaysOfWeek = dto.DateRangeSelectorHasDOW;
        options.includeTimeSelector = dto.SettingTimeRangeSelector;
        options.includeIntervalSelector = dto.SettingIntervalSelector;
        options.includeEmpGroupSelector = dto.SettingEmpSelector;
        options.includeRecGroupSelector = dto.SettingRecSelector;
        options.includeViewBy = dto.GroupSelectorShowToggle;
        options.enableMultiGroupSelect = dto.GroupSelectorHasMulti;
        options.includeMetricSelector = dto.SettingMetricSelector;
        options.includeAttributesSelector = dto.SettingAttSelector;
        options.includeSoloUserAttributesSelector = dto.SettingSoloUserAttSelector;
        options.includeUserAttMetrics = dto.IncludeUserAttMetrics;
        return options;
    }
}

export interface SelectedMetricsChangeEvent {
    type: string;
    pageName: string;
    selectedMetrics: number[];
    metricNames: Record<number, string>;
    metricFullNames: Record<number, string>;
    timeTypeOverrides: Map<number, TimeType>;
    mCT: number;
    result: string;
}

export class ModuleManagerMetricInfo {
    Id: number;
    Abbreviation: string;
    Name: string;
    CustomName?: string;
    ConversionType: number;
    Direction: number;
    MetricType: MetricType;
    QEAttributeFiltersByRSDef?: { [FieldName: string]: FilterableField };
}

export class ModuleManagerDateRangeInfo {
    name: string;
    displayName: string;
    utcOffset: number;

    get displayNameOrCustomDates() {
        if (this.name.includes(' - ')) {
            return this.name;
        }

        return this.displayName;
    }

    get numberOfDays(): number {
        const startDateDays = Math.floor(this.startDate.getTime() / 1000 / 60 / 60 / 24);
        const endDateDays = Math.floor(this.endDate.getTime() / 1000 / 60 / 60 / 24);
        return (endDateDays - startDateDays) + 1;
    }

    get startDate(): Date {
        let today = new Date().addHours(this.utcOffset + new Date().getTimezoneOffset() / 60);
        let todayCopy = new Date().addHours(this.utcOffset + new Date().getTimezoneOffset() / 60); // so we don't change "today" when we move it
        let thisWeekStart = today.getDay() == window['CVWeekStart'] ? today : todayCopy.moveToDayOfWeek(window['CVWeekStart'], -1);

        switch (this.name) {
            case "Today":
                return today;
            case "This Week":
                return thisWeekStart;
            case "FCR 30 Day":
                return today.addDays(-4 - 30);
            case "This Month":
                return today.moveToFirstDayOfMonth();
            case "Yesterday":
                return today.addDays(-1);
            case "Last 7 Days":
                return today.addDays(-6);
            case "Last 30 Days":
                return today.addDays(-29);
            case "Last 90 Days":
                return today.addDays(-89);
            case "Yesterday 30 Days":
                return today.addDays(-90);
            case "Last Week":
                return thisWeekStart.clone().addDays(-7);
            case "Last 4 Weeks":
                return thisWeekStart.clone().addDays(-21);
            case "Last 8 Weeks":
                return thisWeekStart.clone().addDays(-49);
            case "Last Month":
                return today.moveToFirstDayOfMonth().addMonths(-1);
            case "Last Year":
                return today.getMonth() == 0 ? today.moveToFirstDayOfMonth().addYears(-1) : today.moveToMonth(0, -1).moveToFirstDayOfMonth().addYears(-1);
            case "YTD":
                return today.getMonth() == 0 ? today.moveToFirstDayOfMonth() : today.moveToMonth(0, -1).moveToFirstDayOfMonth();
            case "Previous 15":
                if (today.getDate() >= 16)
                    return today.moveToFirstDayOfMonth();
                else
                    return today.moveToFirstDayOfMonth().addMonths(-1).addDays(15);
            case "Current 15":
                if (today.getDate() >= 16)
                    return new Date(today.setDate(16));
                else
                    return today.moveToFirstDayOfMonth();
            case "Rolling Year": //Updated to be Rolling 12 months
                const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                return new Date(firstDayOfCurrentMonth.setMonth(firstDayOfCurrentMonth.getMonth() - 12));
            case "This Quarter":
                let startOfQuarter = (Math.floor((today.getMonth()) / 3)) * 3;
                return new Date(today.getFullYear(), startOfQuarter, 1);
            case "Last Quarter":
                let lastQuarterStart = ((Math.floor((today.getMonth()) / 3)) - 1) * 3;
                return new Date(today.getFullYear(), lastQuarterStart, 1);
            default:
                let dates = this.name.split(' - ');
                if (dates.length < 2)
                    return today;
                else {
                    const startDate = Date.parseExact(dates[0], window['CVAngularDateFormat']) ?? today;
                    if (startDate > today)
                        return today;
                    else 
                        return startDate;
                }
        }
    }

    get endDate(): Date {
        let today = new Date().addHours(this.utcOffset + new Date().getTimezoneOffset() / 60);
        let todayCopy = new Date().addHours(this.utcOffset + new Date().getTimezoneOffset() / 60); // so we don't change "today" when we move it
        let thisWeekStart = today.getDay() == window['CVWeekStart'] ? today : todayCopy.moveToDayOfWeek(window['CVWeekStart'], -1);

        switch (this.name) {
            case "Today":
            case "Last 7 Days":
            case "Last 30 Days":
            case "Last 90 Days":
            case "Rolling Year":
            case "YTD":
                    return today;
            case "This Week":
                return thisWeekStart.clone().addDays(6);
            case "This Month":
                return today.moveToLastDayOfMonth();
            case "Yesterday":
            case "Yesterday 30 Day":
                return today.addDays(-1);
            case "Current 15":
                if (today.getDate() >= 16)
                    return today.moveToLastDayOfMonth();
                else
                    return today.moveToFirstDayOfMonth().addDays(14);
            case "Previous 15":
                if (today.getDate() >= 16)
                    return today.moveToFirstDayOfMonth().addDays(14);
                else
                    return today.moveToFirstDayOfMonth().addDays(-1);
            case "This Quarter":
                let startOfQuarter = (Math.floor((today.getMonth()) / 3)) * 3;
                return (new Date(today.getFullYear(), startOfQuarter + 2, 1)).moveToLastDayOfMonth();
            case "Last Week":
                return thisWeekStart.clone().addDays(-1);
            case "Last 4 Weeks":
            case "Last 8 Weeks":
                return thisWeekStart.clone().addDays(-1).addDays(7);
            case "Last Month":
                return today.moveToFirstDayOfMonth().addDays(-1);
            case "Last Year":
                return today.getMonth() == 0 ? today.moveToFirstDayOfMonth().addDays(-1) : today.moveToMonth(0, -1).moveToFirstDayOfMonth().addDays(-1);
            case "FCR 30 Day":
                return today.addDays(-4);
            case "Last Quarter":
                let lastQuarterStart = ((Math.floor((today.getMonth()) / 3)) - 1) * 3;
                return (new Date(today.getFullYear(), lastQuarterStart + 2, 1)).moveToLastDayOfMonth();
            default:
                let dates = this.name.split(' - ');
                if (dates.length < 2)
                    return today;
                else {
                    const endDate = Date.parseExact(dates[1], window['CVAngularDateFormat']) ?? today;
                    if (endDate > today)
                        return today;
                    else 
                        return endDate;
                }
        }
    }

    static fromObject(obj: { name: string, displayName: string, utcOffset: number }) {
        let info = new ModuleManagerDateRangeInfo();
        info.name = obj.name;
        info.displayName = obj.displayName;
        info.utcOffset = obj.utcOffset;
        return info;
    }

    toBareMetricsPreset(): BareMetricsCalendarPreset {
        if (this.name == "Custom...")
            return;

        return {
            name: this.name,
            label: this.displayName,
            start: this.startDate,
            end: this.endDate,
        }
    }
}

export enum DOW {
    Sunday = 1 << 0,
    Monday = 1 << 1,
    Tuesday = 1 << 2,
    Wednesday = 1 << 3,
    Thursday = 1 << 4,
    Friday = 1 << 5,
    Saturday = 1 << 6,
    All = 127, // 0111-1111 means all of above are selected
}

export enum TimeInterval {
    HalfHour = 0,
    Hour = 1,
    Day = 2,
    DayOfWeek = 3,
    Week = 4,
    Month = 5,
    Year = 6,
    FifteenMinute = 7,
}

export class ModuleManagerModuleSettings {
    /** Custom setting values */
    [settingName: string]: any;

    /** Set to true only when the module is first created */
    isNewModule = false;
    /** The group type selected by the group selector */
    selectedGroupType: BaseGroupType;
    /** The level selected by the selected group selector */
    selectedGroupLevel: number;
    /** The name of the level selected by the selected group selector */
    selectedGroupLevelName: string;
    /** The name of the parent level selected by the selected group selector */
    parentGroupLevelName: string;
    /** The display name for the selected group(s) */
    selectedGroupName: string;
    /** The max level for the selected group selector */
    maxGroupLevel: number;
    /** The refresh type determined from date range and metrics. */
    refreshType: ModuleManagerRefreshType;
    /** ID used in certain modules for an ID of an object needed by them */
    relatedId?: number;
    /** List of selected metrics with id and names */
    selectedMetrics: ModuleManagerMetricInfo[];
    /** Indicates numeric metric types that the widget will ignore */
    disabledNumericMetrics: number[];
    /** Indicates if the date picker needs to be hidden */
    hideDatePicker: boolean;
    selectedFilterableGroupingDTO?: FilterableGroupingDTO;

    userAttributesModel: AttributeSelectorModel;
    dataAttributesModel: AttributeSelectorModel;

    empSelectorDTO: GroupSelectorDTO;
    recSelectorDTO: GroupSelectorDTO;

    selectedDateRange: ModuleManagerDateRangeInfo;
    isTimeSelectorEnabled: boolean;
    invertTimeSelector: boolean;
    selectedDaysOfWeek: DOW;
    minTime: number;
    maxTime: number;
    intervalAmt: number;
    intervalType: TimeInterval;
    fifteenMinutesIntervalVisible: boolean;
}

export class ModuleManagerGenericSettingsControls {
    empSelectorHTML: string;
    recSelectorHTML: string;
    metricSelectorHTML: string;
    attrSelectorHTML: string;
    dateRangeSelectorHTML: string;
    timeRangeSelectorHTML: string;
    intervalSelectorHTML: string;
    availableDateRanges: ModuleManagerDateRangeInfo[];
    controlSettings: ModuleManagerModuleSettings;
    moduleState: Record<string, any>;
}

export interface IModuleManagerSettingsComponent {
    /** State for tracking if settings are loading */
    settingsLoading: boolean;
    /** State for tracking if generic settings are loading */
    genericSettingsLoading: boolean;
    /** State for tracking if generic settings have been loaded */
    genericSettingsLoaded: boolean;
    settingsManager?: SlideoutSettingsManager;

    setGenericSettingsControlSettings(controlSettings: ModuleManagerModuleSettings): void;
    setGenericSettingsHtml(data: ModuleManagerGenericSettingsControls): void;
    applySettings(): void;
    cancelSettings(): void;
}

export enum ModuleSettingsView {
    Modal = "modal",
    Slideout = "slideout",
    CXOneModal = "cxOneModal",
}

export interface ModuleSettingsAppliedModel {
    waitBeforeSaving?: Promise<any>,
    settingsManager?: SlideoutSettingsManager,
    fromModal?: boolean,
    fromDetailedDisplay?: boolean,
}

export interface WorkspaceApiModel {
    WorkspaceKey: string;
    Dashboards: Dashboard[]; // dashboard model not needed in front-end yet.
    MaxDashboardCount: number;
    ActiveDashboard: string;
    UserPermissions: {[key: string]: boolean};
    UseNewSettings: boolean;
    IsNewPlatform: boolean;
}
